import themeConfig from '@configs/themeConfig';
import { ChevronLeft, Lock, ChevronUp, ChevronDown } from 'react-feather';
import { Button, Row, Spinner } from 'reactstrap';
import { getOfficeLocations } from '../../../../../apis/appointment.api';
import { useQuery } from 'react-query';
import { useEffect, useState, useRef } from 'react';
import useShop from '../../../../../utility/hooks/useShop';
import { useRecoilState } from 'recoil';
import userState from '../../../../../recoil/atoms/auth/user';

// Utility function to calculate dynamic font size
const calculateFontSize = (text, maxWidth, maxHeight, baseFontSize = 16) => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Try base font size first
    context.font = `${baseFontSize}px Spartan`;
    const textMetrics = context.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = baseFontSize * 1.2; // Account for line height

    // If text fits, return base font size
    if (textWidth <= maxWidth && textHeight <= maxHeight) {
      return baseFontSize;
    }

    // Calculate scale factors
    const widthScale = maxWidth / textWidth;
    const heightScale = maxHeight / textHeight;
    const scale = Math.min(widthScale, heightScale, 1); // Don't scale up, only down

    const calculatedSize = Math.max(baseFontSize * scale, 8); // Minimum font size of 10px

    // Verify the calculated size works
    context.font = `${calculatedSize}px Spartan`;
    const finalMetrics = context.measureText(text);

    // If still too wide, try a smaller size
    if (finalMetrics.width > maxWidth) {
      return Math.max(calculatedSize * 0.9, 10);
    }

    return calculatedSize;
  } catch (error) {
    // Fallback: simple calculation based on text length
    const textLength = text.length;
    if (textLength > 30) return 12;
    if (textLength > 20) return 14;
    return baseFontSize;
  }
};

const LocationSelection = ({ goNextStep, booking, setStep, setBooking, backPreviousStep }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [fontSizes, setFontSizes] = useState({});
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  const shop = useShop();
  const [user, setUser] = useRecoilState(userState);
  const buttonRefs = useRef({});
  const scrollContainerRef = useRef(null);

  const {
    data,
    refetch,
  } = useQuery(['getLocations'], () => getOfficeLocations(), {
    onSuccess: (data) => {
      if (data?.data) {
        setLocations(data.data);
      }
      setIsLoading(false);
    },
    staleTime: 10000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    refetch();
  }, []);

  // Calculate font sizes when locations change or window resizes
  useEffect(() => {
    const calculateSizes = () => {
      if (!locations || locations.length === 0) return;

      const newFontSizes = {};
      let hasValidRefs = false;

      locations.forEach(location => {
        if (!location || !location.id) return;

        const buttonRef = buttonRefs.current[location.id];
        if (buttonRef && typeof buttonRef.getBoundingClientRect === 'function') {
          try {
            const rect = buttonRef.getBoundingClientRect();
            if (rect && rect.width > 0 && rect.height > 0) {
              const maxWidth = rect.width - 32; // Account for padding
              const maxHeight = rect.height - 16; // Account for padding
              const fontSize = calculateFontSize(location.name, maxWidth, maxHeight);
              newFontSizes[location.id] = fontSize;
              hasValidRefs = true;
            }
          } catch (error) {
            // console.log('Error calculating font size for location:', location.id, error);
          }
        }

        // Fallback: use simple text length calculation if no valid ref
        if (!newFontSizes[location.id]) {
          const textLength = location.name.length;
          if (textLength > 70) newFontSizes[location.id] = 12;
          else if (textLength > 60) newFontSizes[location.id] = 14;
          else if (textLength > 50) newFontSizes[location.id] = 14;
          else newFontSizes[location.id] = 16;
        }
      });

      // Update font sizes if we have any calculations
      if (Object.keys(newFontSizes).length > 0) {
        setFontSizes(newFontSizes);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const calculateWithRAF = () => {
      requestAnimationFrame(() => {
        // Check if any refs are available
        const anyRefsAvailable = locations.some(location =>
          location && location.id && buttonRefs.current[location.id]
        );

        if (anyRefsAvailable) {
          calculateSizes();
        } else {
          // If no refs are ready, try again after a short delay
          setTimeout(calculateWithRAF, 50);
        }
      });
    };

    // Start the calculation process
    calculateWithRAF();

    // Debounced resize handler
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateSizes, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [locations]);

  // Handle scroll events to show/hide indicators
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

      // Show top indicator if scrolled down
      setShowTopIndicator(scrollTop > 10);

      // Show bottom indicator if there's more content below
      setShowBottomIndicator(scrollTop + clientHeight < scrollHeight - 10);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Check initial state
      handleScroll();
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [locations]);

  return (
    <div className="client-type-selection">
      <div className="d-flex align-items-center booking-header" style={{ paddingTop: '8px' }}>
      </div>
      {!user ? (
        <Row>
          <Button className="btn-icon rounded-circle mr-50 mb-0 pt-0" color="gray" onClick={backPreviousStep}>
            <ChevronLeft />
          </Button>
        </Row>
      ) : (
        <div className='mt-2' />
      )}
      <div className="header">
        <span
          className="header-title"
        >
          At which location do you want to schedule your pet's appointment?
        </span>
        <hr />
        <div className="buttons-container">
          {isLoading ? (
            <div
              className="d-flex justify-content-center"
            >
              <Spinner
                color="primary"
              />
            </div>
          ) : (
            <div className="location-scroll-container" style={{ position: 'relative' }}>
              {/* Top scroll indicator */}
              {showTopIndicator && (
                <div
                  className="scroll-indicator scroll-indicator-top"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '24px',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{
                    background: 'rgba(110, 107, 123, 0.1)',
                    borderRadius: '12px',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ChevronUp size={18} color="#6e6b7b" />
                  </div>
                </div>
              )}

              {/* Bottom scroll indicator */}
              {showBottomIndicator && (
                <div
                  className="scroll-indicator scroll-indicator-bottom"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '24px',
                    background: 'linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{
                    background: 'rgba(110, 107, 123, 0.1)',
                    borderRadius: '12px',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ChevronDown size={18} color="#6e6b7b" />
                  </div>
                </div>
              )}

              <div
                ref={scrollContainerRef}
                className='d-flex flex-column align-items-center'
                style={{
                  maxHeight: 'min(65vh, 650px)',
                  overflowY: 'auto',
                  paddingTop: showTopIndicator ? '10px' : '0',
                  paddingBottom: showBottomIndicator ? '10px' : '0'
                }}
              >
                {locations?.length === 0 && (
                  <>
                  {`No locations found. Please contact ${shop?.name} to book an appointment.`}
                  </>
                )}
                {locations?.length > 0 && (
                  <>
                    {(locations?.map(
                      (location) => (
                        <Button
                          ref={(el) => {
                            if (el) {
                              buttonRefs.current[location.id] = el;
                            } else {
                              delete buttonRefs.current[location.id];
                            }
                          }}
                          id={`location-btn-${location.id}`}
                          key={location.id}
                          className="button location-button"
                          color="primary"
                          outline
                          onClick={() => {
                            setStep(2);
                            setBooking(prev => ({ ...prev, location: { ...location } }));
                            goNextStep();
                          }}
                          style={{
                            fontSize: `${fontSizes[location.id] || 14}px`,
                            padding: '8px 16px',
                            minHeight: '68px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            lineHeight: '1.2'
                          }}
                          dangerouslySetInnerHTML={{ __html: location.name }}
                        />
                      ),
                    )
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSelection;
